<template>
  <div class="feed-alert-card">
    <!-- Header Section - Timer + Task Name -->
    <div class="alert-header">
      <div class="alert-title-section">
        <h3 class="alert-title">{{ (alert && alert.title) || (alert && alert.designator) || activeTaskName || 'Loading...' }}</h3>
      </div>

      <!-- Timer Display -->
      <div v-if="shouldShowTimer" class="timer-display" :class="{'timer-warning': isTimerOverdue}">
        <span class="timer-icon">⏱</span>
        <span class="timer-text">{{ displayPassedTime }}</span>
      </div>
    </div>

    <!-- SelectTask Component - Only shown in Manual Mode -->
    <div v-if="!isFeedMode && !followUpMode" class="select-task-wrapper">
      <select-task
        :alert-id="alert && alert.id"
        :child-task-complete="childTaskComplete"
        :has-active-task="!waitingForTaskSelection && (!!activeTaskName || !!activeTaskId)"
        @task-selected="onTaskSelected"
        @task-finished="onTaskFinished"
        @timing-updated="onTimingUpdated"
      />
    </div>

    <!-- Server-side reason the feed is empty (Monday has no slot, server error) -->
    <div v-if="feedNotice" class="feed-notice">
      <span class="feed-notice-icon">ℹ</span>
      <span class="feed-notice-text">{{ feedNotice }}</span>
      <button class="button is-small is-light feed-notice-retry" @click="getAlert()">נסה שוב</button>
    </div>

    <!-- Waiting for Task Selection Message -->
    <div v-if="waitingForTaskSelection && !followUpMode" class="waiting-message">
      <div class="waiting-content">
        <span class="waiting-icon">⏳</span>
        <p class="waiting-text">ממתין לבחירת משימה</p>
      </div>
    </div>

    <!-- Dynamic Task Component Loading -->
    <div v-else-if="activeTaskName" class="task-component-wrapper">
      <!-- Designated Position Component - Check BEFORE Static Tasks -->
      <designated-position
        v-if="activeTaskName === 'משרה ייעודית'"
        :alert="alert"
        :monday-task-data="mondayTaskData"
        @reload-same-task="onReloadSameTask"
        @scoring-completed="onScoringCompleted"
        @position-changed="onPositionChanged"
      />

      <!-- Static Tasks Component -->
      <static-tasks
        v-else-if="activeTaskId && isStaticTask(activeTaskId)"
        :task-id="activeTaskId"
        :is-feed-mode="isFeedMode"
        @go-to-next-task="handleGoToNextTask"
      />

      <!-- Bubble Component (Blue/Yellow/Purple/Random Submissions) -->
      <bubble
        v-else-if="activeTaskName && (activeTaskName.toLowerCase().includes('bubble') ||
                   activeTaskName.toLowerCase().includes('בועה') ||
                   activeTaskName.toLowerCase().includes('הגשות רנדומלי') ||
                   activeTaskId === 'random_submissions_feed')"
        :alert="alert"
        :monday-task-data="mondayTaskData"
        @task-completed="onBubbleTaskCompleted"
        @checklist-status-changed="onChecklistStatusChanged"
        @reload-same-task="onReloadSameTask"
      />

      <!-- FollowUp Component -->
      <follow-up
        v-else-if="activeTaskName && (activeTaskName.toLowerCase().includes('follow') ||
                   activeTaskName.toLowerCase().includes('פולאו') ||
                   activeTaskName.toLowerCase().includes('פולואפ'))"
        :alert="alert"
        :monday-task-data="mondayTaskData"
        :selected-task-name="activeTaskName"
        @reload-same-task="onReloadSameTask"
      />

      <!-- GenericTask Component - Fallback for other tasks -->
      <generic-task
        v-else
        :task-name="activeTaskName"
        :is-feed-mode="isFeedMode"
        @go-to-next-task="handleGoToNextTask"
      />
    </div>

    <!-- Bottom Actions Bar -->
    <div class="alert-actions">
      <!-- Quick Actions Row -->
      <div class="quick-actions-row">
        <button @click="nextContact" class="button is-info is-small">
          <span class="icon is-small"><span>📅</span></span>
          <span>Next Contact</span>
        </button>
        <button @click="openCompanySuitcase" class="button is-info is-small">
          <span class="icon is-small"><span>⚙️</span></span>
          <span>Config</span>
        </button>
        <button
          @click="toggleFollowUpMode"
          class="button is-small"
          :class="followUpMode ? 'is-danger' : 'is-info'"
        >
          <span class="icon is-small"><span>🔄</span></span>
          <span>Follow Up Mode</span>
        </button>
      </div>

      <!-- Main Actions Row -->
      <div class="main-actions-row">
        <!-- Show "Switch to Manual Mode" button only in Feed Mode -->
        <button v-if="isFeedMode" class="button is-info" @click="switchToManualMode">
          <span class="icon"><span>🔧</span></span>
          <span>מצב ידני</span>
        </button>

        <!-- Show "Back to Feed" button only in Manual Mode -->
        <button v-if="!isFeedMode" class="button is-success" @click="switchToFeedMode">
          <span class="icon"><span>🔄</span></span>
          <span>חזרה לפיד</span>
        </button>

        <button class="button is-danger" @click="reportError">
          <span class="icon"><span>🚫</span></span>
          <span>טעות בהצעה</span>
        </button>

        <button class="button is-warning" @click="edit">
          <span class="icon"><span>✏️</span></span>
          <span>עריכה</span>
        </button>
        <button class="button is-info" @click="onBreak">
          <span class="icon"><span>☕</span></span>
          <span>הפסקה</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import SelectTask from './SelectTask.vue'
import DesignatedPosition from './DesignatedPosition.vue'
import Bubble from './Bubble.vue'
import FollowUp from './FollowUp.vue'
import StaticTasks from './StaticTasks.vue'
import GenericTask from './GenericTask.vue'
import taskTimeConfig from '../config/task-time-config.json'

export default {
  name: 'FeedAlerts',
  components: {
    SelectTask,
    DesignatedPosition,
    Bubble,
    FollowUp,
    StaticTasks,
    GenericTask
  },
  data () {
    return {
      requestVersion: 0,
      alert: null,
      startTime: localStorage.getItem('missionStartTime'),
      taskStartTime: localStorage.getItem('taskStartTime'),
      passedTimeMinutes: 0,
      taskPassedTimeSeconds: 0,
      timer: null,
      followUpMode: localStorage.getItem('followUpMode') == 'true',
      isFeedMode: localStorage.getItem('isFeedMode') !== 'false',
      selectedTaskName: localStorage.getItem('feedAlertSelectedTaskName') || null,
      selectedTaskId: localStorage.getItem('feedAlertSelectedTaskId') || null,
      mondayTaskData: null,
      feedNotice: null,
      waitingForTaskSelection: false,
      staticTaskIds: ['morning_emails', 'noon_emails', 'evening_emails', 'recruit_employers', 'collections', 'project_1000', 'lead_board_tracking'],
      childTaskComplete: false,
      taskTimeConfig: taskTimeConfig
    }
  },
  computed: {
    activeTaskName () {
      return this.followUpMode ? 'פולואפ כללי' : this.selectedTaskName
    },
    activeTaskId () {
      return this.followUpMode ? 'general_follow_up' : this.selectedTaskId
    },
    displayPassedTime () {
      // Display task-specific time (not mission time)
      const totalSeconds = this.taskPassedTimeSeconds
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    },
    isTimerOverdue () {
      // Show red after 1 minute (60 seconds)
      return this.taskPassedTimeSeconds > 60
    },
    shouldShowTimer () {
      // Hide timer for designated tasks ("משרה ייעודית")
      if (this.activeTaskName === 'משרה ייעודית') {
        return false
      }
      return true
    }
  },
  created () {
    if (this.startTime) {
      const missionStartMoment = this.$moment(this.startTime)
      const hoursSinceMissionStart = this.$moment().diff(missionStartMoment, 'hours')

      if (hoursSinceMissionStart > 12) {
        localStorage.removeItem('missionStartTime')
        localStorage.removeItem('missionBreakTimes')
        this.startTime = null
      }
    }

    if (this.isFeedMode) {
      this.handleAutomaticModeRefresh()
    } else {
      this.handleManualModeRefresh()
    }

    // Start recording monitor polling for recruiters with synced phone recordings.
    if (['מעין סלוק', 'נעם מועלם', 'זהר מילר'].includes(this.$utils.getOwner().name)) {
      this.$store.dispatch('recordingMonitor/startPolling', { vm: this })
    }
  },
  methods: {
    ...mapMutations('modals', ['create', 'removeModalById']),

    async handleAutomaticModeRefresh () {
      if (this.followUpMode) return this.loadFollowUp()
      const requestVersion = ++this.requestVersion
      const params = {
        employee_name: this.$utils.getOwner().name,
        owner: this.$utils.getOwner().name,
        current_time: new Date().toISOString(),
        operation: this.$settings.namespace
      }

      console.log(`📤 handleAutomaticModeRefresh - שולח operation: ${params.operation}`)

      try {
        const serverAlert = await this.$network_utils.getAlertForFeedWithMonday(params)
        if (requestVersion !== this.requestVersion) return
        const mondayTaskName = (serverAlert.monday_task && serverAlert.monday_task.task_name) || serverAlert.task_name
        const cachedTaskName = localStorage.getItem('feedAlertSelectedTaskName')

        if (mondayTaskName === 'משרה ייעודית' && cachedTaskName === 'משרה ייעודית') {
          const storedState = localStorage.getItem('designated_position_state')
          if (storedState) {
            try {
              const parsedState = JSON.parse(storedState)
              // Only restore if we have valid position data
              if (parsedState.position_id) {
                await this.getAlertWithDesignatedState(parsedState)
                return
              } else {
                // No position data - clear and let server response handle it
                console.log('No position_id in localStorage - clearing state')
                localStorage.removeItem('designated_position_state')
              }
            } catch (e) {
              console.error('Error parsing designated_position_state:', e)
              localStorage.removeItem('designated_position_state')
            }
          }
        }

        this.clearTaskLocalStorage()
        this.processServerResponse(serverAlert)
      } catch (error) {
        if (requestVersion !== this.requestVersion) return
        console.error('Error in automatic mode refresh:', error)
        this.alert = null
        this.mondayTaskData = null
      }
    },

    async handleManualModeRefresh () {
      if (this.followUpMode) return this.loadFollowUp()
      const cachedTaskName = localStorage.getItem('feedAlertSelectedTaskName')

      if (cachedTaskName) {
        if (this.taskStartTime) {
          const taskStartMoment = this.$moment(this.taskStartTime)
          const hoursSinceStart = this.$moment().diff(taskStartMoment, 'hours')

          if (hoursSinceStart > 24) {
            const newTaskStartTime = new Date()
            this.taskStartTime = newTaskStartTime
            localStorage.setItem('taskStartTime', newTaskStartTime.toISOString())
            this.taskPassedTimeSeconds = 0
          }
        }

        this.getAlert(cachedTaskName)
      } else {
        localStorage.removeItem('taskStartTime')
        this.taskStartTime = null
        this.taskPassedTimeSeconds = 0
        this.waitingForTaskSelection = true
      }
    },

    async getAlertWithDesignatedState (parsedState) {
      if (this.followUpMode) return this.loadFollowUp()
      const requestVersion = ++this.requestVersion
      const params = {
        employee_name: this.$utils.getOwner().name,
        owner: this.$utils.getOwner().name,
        current_time: new Date().toISOString(),
        task_name: 'משרה ייעודית',
        position_id: parsedState.position_id,
        offer_type: parsedState.offer_type,
        counter: parsedState.counter
      }

      const serverAlert = await this.$network_utils.getAlertForFeedWithMonday(params)
      if (requestVersion !== this.requestVersion) return
      this.processServerResponse(serverAlert)
    },

    processServerResponse (serverAlert) {
      if (this.followUpMode) {
        this.feedNotice = this.describeEmptyFeed(serverAlert)
        const alertData = serverAlert && (serverAlert.admin_v1_alert || (serverAlert.id && serverAlert))
        this.alert = this.feedNotice ? null : (alertData || null)
        this.mondayTaskData = null
        if (!this.alert && !this.feedNotice) this.feedNotice = 'אין כרגע מועמדים לפולואפ.'
        this.$emit('input', (this.alert && this.alert.id) || null)
        // No offer-id prefetch in follow-up mode: the queue is applicant-level and
        // the server picks the next candidate at serve time (oldest activity first).
        return
      }
      if (serverAlert.static_task && serverAlert.task_name) {
        this.mondayTaskData = {
          task_name: serverAlert.task_name,
          start_time: serverAlert.start_time,
          end_time: serverAlert.end_time
        }

        if (this.isFeedMode) {
          this.selectedTaskName = serverAlert.task_name
          localStorage.setItem('feedAlertSelectedTaskName', serverAlert.task_name)

          const taskNameToId = {
            'מיילים בוקר': 'morning_emails',
            'שגרת בוקר': 'morning_routine',
            'מיילים צהרים': 'noon_emails',
            'מיילים ערב': 'evening_emails'
          }

          const taskId = taskNameToId[serverAlert.task_name]
          if (taskId) {
            this.selectedTaskId = taskId
            localStorage.setItem('feedAlertSelectedTaskId', taskId)
          }

          this.waitingForTaskSelection = false
          this.alert = null
        }
        return
      }

      if (serverAlert.monday_task) {
        this.mondayTaskData = serverAlert.monday_task

        if (this.isFeedMode && serverAlert.monday_task.task_name) {
          this.selectedTaskName = serverAlert.monday_task.task_name
          localStorage.setItem('feedAlertSelectedTaskName', serverAlert.monday_task.task_name)

          if (serverAlert.monday_task.task_id) {
            this.selectedTaskId = serverAlert.monday_task.task_id
            localStorage.setItem('feedAlertSelectedTaskId', serverAlert.monday_task.task_id)
          }

          this.waitingForTaskSelection = false
        }
      }

      let alertData = serverAlert
      if (serverAlert.admin_v1_alert) {
        alertData = serverAlert.admin_v1_alert
      }

      this.alert = alertData
      this.$emit('input', (alertData && alertData.id) || null)

      // Track this offer as seen to prevent follow-up loop
      const seenOfferId = alertData && alertData.resources && alertData.resources.offer && alertData.resources.offer.id
      if (seenOfferId) {
        this.$store.commit('feedPrefetch/addSeenOfferId', seenOfferId)
      }

      // Prefetch next offer in parallel with current offer's AI evaluation
      this.$nextTick(() => { this.triggerPrefetch() })
    },

    clearTaskLocalStorage () {
      localStorage.removeItem('feedAlertSelectedTaskName')
      localStorage.removeItem('feedAlertSelectedTaskId')
      localStorage.removeItem('designated_position_state')
      localStorage.removeItem('taskStartTime')
      this.taskStartTime = null
      this.taskPassedTimeSeconds = 0
    },

    setPassedTime () {
      const breaks = JSON.parse(localStorage.getItem('missionBreakTimes') || '[]')
      this.passedTimeMinutes = this.$moment().diff(this.$moment(this.startTime), 'minutes') - breaks.reduce((prev, curr) => prev + curr, 0)

      // Calculate task-specific time
      if (this.taskStartTime) {
        this.taskPassedTimeSeconds = this.$moment().diff(this.$moment(this.taskStartTime), 'seconds')
      } else {
        this.taskPassedTimeSeconds = 0
      }
    },

    async getAlert (taskName = null) {
      if (this.followUpMode) return this.loadFollowUp()
      const requestVersion = ++this.requestVersion
      clearInterval(this.timer)
      this.timer = setInterval(this.setPassedTime, 1000)

      if (!this.startTime) {
        this.startTime = new Date()
        localStorage.setItem('missionStartTime', this.startTime)
      }

      // Reset task timer when switching to a different task
      // Important: Check against the PREVIOUS task name before updating
      const previousTaskName = this.selectedTaskName
      if (taskName && taskName !== previousTaskName) {
        const newTaskStartTime = new Date()
        this.taskStartTime = newTaskStartTime
        localStorage.setItem('taskStartTime', newTaskStartTime.toISOString())
        this.taskPassedTimeSeconds = 0
      }

      try {
        // Build params with optional task_name
        const params = {
          employee_name: this.$utils.getOwner().name,
          owner: this.$utils.getOwner().name,
          current_time: new Date().toISOString()
        }

        // Add task_name if provided (from SelectTask)
        if (taskName) {
          params.task_name = taskName
          this.selectedTaskName = taskName

          // If this is designated position, send localStorage data
          if (taskName === 'משרה ייעודית') {
            const storedState = localStorage.getItem('designated_position_state')
            if (storedState) {
              try {
                const parsedState = JSON.parse(storedState)
                params.position_id = parsedState.position_id
                params.offer_type = parsedState.offer_type
                params.counter = parsedState.counter
              } catch (e) {
              }
            } else {
              params.position_id = null
              params.offer_type = 'related_positions'
              params.counter = 1
            }
          }
        }

        // 🔵 LOG: Before server call
        const taskDisplayName = taskName || 'כללי'
        const requestStartTime = Date.now()
        console.log(`\n🔵 ═══ הולך לשרת להביא אלרט ═══`)
        console.log(`📋 משימה: ${taskDisplayName}`)
        console.log(`👤 עובד: ${params.employee_name}`)

        const cachedPositionTitle = localStorage.getItem('current_position_title')
        if (cachedPositionTitle) {
          console.log(`🎯 משרה: ${cachedPositionTitle}`)
        }

        if (params.offer_type) {
          console.log(`📊 סוג הצעה: ${params.offer_type}`)
        }
        console.log(`════════════════════════════════\n`)

        // Add operation (app name) to params
        params.operation = this.$settings.namespace
        console.log(`📤 getAlert - שולח operation: ${params.operation}`)

        // Call server with task_name parameter
        this.feedNotice = null
        const serverAlert = await this.$network_utils.getAlertForFeedWithMonday(params)
        if (requestVersion !== this.requestVersion) return

        // 🟢 LOG: After server response
        const requestEndTime = Date.now()
        const durationMs = requestEndTime - requestStartTime
        const durationMinutes = Math.floor(durationMs / 60000)
        const durationSeconds = ((durationMs % 60000) / 1000).toFixed(2)

        const alertId = (serverAlert && serverAlert.admin_v1_alert && serverAlert.admin_v1_alert.id) || (serverAlert && serverAlert.id) || 'N/A'
        const offerId = (serverAlert && serverAlert.admin_v1_alert && serverAlert.admin_v1_alert.resources && serverAlert.admin_v1_alert.resources.offer && serverAlert.admin_v1_alert.resources.offer.id) ||
                       (serverAlert && serverAlert.resources && serverAlert.resources.offer && serverAlert.resources.offer.id) || 'N/A'
        const positionTitle = (serverAlert && serverAlert.admin_v1_alert && serverAlert.admin_v1_alert.resources && serverAlert.admin_v1_alert.resources.position && serverAlert.admin_v1_alert.resources.position.title) ||
                            (serverAlert && serverAlert.resources && serverAlert.resources.position && serverAlert.resources.position.title) || 'N/A'
        const applicantName = (serverAlert && serverAlert.admin_v1_alert && serverAlert.admin_v1_alert.resources && serverAlert.admin_v1_alert.resources.applicant && serverAlert.admin_v1_alert.resources.applicant.full_name) ||
                             (serverAlert && serverAlert.resources && serverAlert.resources.applicant && serverAlert.resources.applicant.full_name) || 'N/A'

        console.log(`\n🟢 ═══ התקבלה תשובה מהשרת ═══`)
        console.log(`📋 משימה: ${taskDisplayName}`)
        console.log(`⏱ קריאת השרת לקחה: ${durationMinutes} דקות ו-${durationSeconds} שניות`)
        console.log(`🆔 Alert ID: ${alertId}`)
        if (offerId !== 'N/A') {
          console.log(`💼 Offer ID: ${offerId}`)
        }
        if (positionTitle !== 'N/A') {
          console.log(`🎯 משרה: ${positionTitle}`)
          localStorage.setItem('current_position_title', positionTitle)
        } else {
          localStorage.removeItem('current_position_title')
        }
        if (applicantName !== 'N/A') {
          console.log(`👤 מועמד: ${applicantName}`)
        }
        console.log(`════════════════════════════════\n`)

        this.feedNotice = this.describeEmptyFeed(serverAlert)
        if (this.feedNotice) {
          this.alert = null
          this.mondayTaskData = null
          this.$emit('input', null)
          return
        }

        if (serverAlert.static_task && serverAlert.task_name) {
          console.log('[Prefetch] getAlert: static_task detected, early return. task_name:', serverAlert.task_name)
          this.mondayTaskData = {
            task_name: serverAlert.task_name,
            start_time: serverAlert.start_time,
            end_time: serverAlert.end_time
          }

          if (this.isFeedMode) {
            this.selectedTaskName = serverAlert.task_name
            localStorage.setItem('feedAlertSelectedTaskName', serverAlert.task_name)

            const taskNameToId = {
              'מיילים בוקר': 'morning_emails',
              'שגרת בוקר': 'morning_routine',
              'מיילים צהרים': 'noon_emails',
              'מיילים ערב': 'evening_emails'
            }

            const taskId = taskNameToId[serverAlert.task_name]
            if (taskId) {
              this.selectedTaskId = taskId
              localStorage.setItem('feedAlertSelectedTaskId', taskId)
            }

            this.waitingForTaskSelection = false
            this.alert = null
          }
          return
        }

        console.log('[Prefetch] getAlert: no static_task, proceeding to set alert. monday_task:', !!serverAlert.monday_task)

        // Extract Monday task data from response
        if (serverAlert.monday_task) {
          this.mondayTaskData = serverAlert.monday_task

          if (this.isFeedMode && serverAlert.monday_task.task_name) {
            this.selectedTaskName = serverAlert.monday_task.task_name
            localStorage.setItem('feedAlertSelectedTaskName', serverAlert.monday_task.task_name)

            if (serverAlert.monday_task.task_id) {
              this.selectedTaskId = serverAlert.monday_task.task_id
              localStorage.setItem('feedAlertSelectedTaskId', serverAlert.monday_task.task_id)
            }

            this.waitingForTaskSelection = false
          }
        }

        // Extract alert data
        let alertData = serverAlert
        if (serverAlert.admin_v1_alert) {
          alertData = serverAlert.admin_v1_alert
        }

        this.alert = alertData

        this.$emit('input', (alertData && alertData.id) || null)

        // Prefetch next offer in parallel with current offer's AI evaluation
        this.$nextTick(() => { this.triggerPrefetch() })
      } catch (e) {
        if (requestVersion !== this.requestVersion) return
        console.error(`\n🔴 ═══ שגיאה בקריאת שרת ═══`)
        console.error(`📋 משימה: ${taskName || 'כללי'}`)
        console.error(`❌ שגיאה: ${e.message}`)
        console.error(`════════════════════════════════\n`)
        this.alert = null
        this.mondayTaskData = null
        const status = e && e.response && e.response.status
        this.feedNotice = status === 500
          ? 'השרת לא הצליח להביא את המשימה הבאה (שגיאת שרת). נסה שוב; אם זה חוזר, דווח.'
          : `הפיד לא נטען (${(e && e.message) || 'שגיאה לא ידועה'}). נסה שוב.`
      }
    },

    describeEmptyFeed (serverAlert) {
      if (!serverAlert || typeof serverAlert !== 'object') return 'השרת החזיר תשובה ריקה. נסה שוב.'
      const hasAlert = !!(serverAlert.admin_v1_alert || serverAlert.id || serverAlert.static_task || serverAlert.monday_task)
      if (hasAlert) return null
      if (serverAlert.error === 'No tasks scheduled') {
        return 'אין משימה מתוזמנת עבורך ב-Monday לשעה הזו. בחר/י משימה ידנית (חזרה לפיד → בחירת משימה) או פנה/י למנהל/ת לעדכון הלו"ז.'
      }
      if (serverAlert.scheduled_slot && !serverAlert.task_name) {
        return 'יש לך חלון זמן ב-Monday אבל בלי משימה מוגדרת. בחר/י משימה ידנית.'
      }
      if (serverAlert.message === 'No eligible offers found') {
        return 'אין כרגע הצעות זמינות למשימה הזו (כולן טופלו או נדחו למעקב).'
      }
      if (serverAlert.error || serverAlert.message) {
        return `הפיד ריק: ${serverAlert.error || serverAlert.message}`
      }
      return null
    },

    async finish () {
      localStorage.removeItem('missionStartTime')
      localStorage.removeItem('missionBreakTimes')
      localStorage.removeItem('feedAlertSelectedTaskName')
      localStorage.removeItem('feedAlertSelectedTaskId')
      this.startTime = null
      this.selectedTaskName = null
      this.selectedTaskId = null

      if (this.alert && this.alert.id) {
        await this.$network_utils.updateAlert({
          id: this.alert.id,
          duration: this.passedTimeMinutes,
          status: 'done'
        })
      }

      this.getAlert()
    },

    async onBreak () {
      this.create({
        designator: 'break-modal',
        component_name: 'BreakStartModal',
        modal_title: 'Break',
        show_close_button: false,
        props: {
          alert: this.alert,
          onSubmit: id => {
            this.getAlert()
            this.removeModalById(id)
          }
        }
      })
    },

    edit () {
      this.create({
        designator: 'alert-form-modal',
        component_name: 'FeedAlertEdit',
        modal_title: 'Edit Alert',
        props: {
          alert: this.alert,
          onSubmit: async (id, reset) => {
            if (reset && this.alert && this.alert.id) {
              await this.$network_utils.updateAlert({
                id: this.alert.id,
                duration: this.passedTimeMinutes
              })
              localStorage.removeItem('missionStartTime')
              localStorage.removeItem('missionBreakTimes')
              this.startTime = null
            }
            this.getAlert()
            this.removeModalById(id)
          }
        }
      })
    },

    nextContact () {
      this.create({
        designator: 'alerts-timing-modal',
        component_name: 'FeedAlertEditTiming',
        modal_title: 'Alert\'s Timing',
        props: {
          alert: this.alert,
          isSnoozeAll: true,
          onSubmit: id => {
            this.getAlert()
            this.removeModalById(id)
          }
        }
      })
    },

    async loadFollowUp () {
      const requestVersion = ++this.requestVersion
      this.feedNotice = null
      this.alert = null
      this.mondayTaskData = null
      this.$emit('input', null)
      const params = {
        employee_name: this.$utils.getOwner().name,
        owner: this.$utils.getOwner().name,
        current_time: new Date().toISOString(),
        operation: this.$settings.namespace,
        task_name: 'פולואפ כללי'
      }
      try {
        const response = await this.$network_utils.getAlertForFeedWithMonday(params)
        if (requestVersion !== this.requestVersion) return
        this.processServerResponse(response)
      } catch (error) {
        if (requestVersion !== this.requestVersion) return
        this.feedNotice = 'הפולואפ לא נטען. נסה שוב.'
      }
    },

    toggleFollowUpMode () {
      this.followUpMode = !this.followUpMode
      localStorage.setItem('followUpMode', this.followUpMode.toString())
      ++this.requestVersion
      this.$store.commit('feedPrefetch/clearQueue')
      this.alert = null
      this.mondayTaskData = null
      this.feedNotice = null
      this.$emit('input', null)
      if (this.followUpMode) return this.loadFollowUp()
      if (this.selectedTaskId && this.isStaticTask(this.selectedTaskId)) return
      if (this.selectedTaskName) return this.getAlert(this.selectedTaskName)
      if (this.isFeedMode) return this.handleAutomaticModeRefresh()
      this.waitingForTaskSelection = true
    },

    openCompanySuitcase () {
      this.create({
        designator: 'alert-form-modal',
        component_name: 'AlertForm',
        data: this.alert,
        init_designator: this.alert && this.alert.designator,
        resource_type: this.alert && this.alert.resource_type,
        resource_id: this.alert && this.alert.resource_id,
        modal_title: 'Alert Form'
      })
    },

    isStaticTask (taskId) {
      return this.staticTaskIds.includes(taskId)
    },

    onTaskSelected (taskInfo) {
      ++this.requestVersion
      // Clear prefetch queue when switching tasks
      this.$store.commit('feedPrefetch/clearQueue')

      // Save selected task info to localStorage
      if (taskInfo.taskName) {
        localStorage.setItem('feedAlertSelectedTaskName', taskInfo.taskName)
        this.selectedTaskName = taskInfo.taskName
      }
      if (taskInfo.taskId) {
        localStorage.setItem('feedAlertSelectedTaskId', taskInfo.taskId)
        this.selectedTaskId = taskInfo.taskId
      }

      // ALWAYS reset task timer when selecting a new task
      const newTaskStartTime = new Date()
      this.taskStartTime = newTaskStartTime
      localStorage.setItem('taskStartTime', newTaskStartTime.toISOString())
      this.taskPassedTimeSeconds = 0

      // Clear waiting state
      this.waitingForTaskSelection = false

      if (this.followUpMode) return this.loadFollowUp()

      // Check if this is a static task
      if (taskInfo.taskId && this.isStaticTask(taskInfo.taskId)) {
        // For static tasks, just set the task ID (task name already set above)
        this.selectedTaskId = taskInfo.taskId
        // Note: taskStartTime is already reset above

        // Scroll to top after DOM updates
        this.$nextTick(() => {
          this.scrollToTop()
        })
      } else {
        // For dynamic tasks, refresh alert with the selected task name
        // The getAlert will handle the timer reset for new tasks
        this.getAlert(taskInfo.taskName)

        // Scroll to top after DOM updates
        this.$nextTick(() => {
          this.scrollToTop()
        })
      }
    },

    onTaskFinished () {
      ++this.requestVersion
      this.$store.commit('feedPrefetch/clearQueue')
      // Reset task timer when finishing task
      localStorage.removeItem('taskStartTime')
      this.taskStartTime = null
      this.taskPassedTimeSeconds = 0

      // Clear task selection from localStorage
      localStorage.removeItem('feedAlertSelectedTaskName')
      localStorage.removeItem('feedAlertSelectedTaskId')

      // Set waiting state to show "waiting for task selection" message
      this.waitingForTaskSelection = true
      this.selectedTaskName = null
      this.selectedTaskId = null
    },

    onTimingUpdated () {
      ++this.requestVersion
      // Reset task timer when updating timing
      localStorage.removeItem('taskStartTime')
      this.taskStartTime = null
      this.taskPassedTimeSeconds = 0

      // Clear task selection from localStorage
      localStorage.removeItem('feedAlertSelectedTaskName')
      localStorage.removeItem('feedAlertSelectedTaskId')

      // Set waiting state to show "waiting for task selection" message
      this.waitingForTaskSelection = true
      this.selectedTaskName = null
      this.selectedTaskId = null
    },

    onScoringCompleted () {
      // Handle scoring completion if needed
    },

    onPositionChanged (newPosition) {
      // Update the alert's position in the resources object
      if (this.alert) {
        if (!this.alert.resources) {
          this.alert.resources = {}
        }
        this.alert.resources.position = newPosition
      }
    },

    onBubbleTaskCompleted () {
      // The Bubble component has completed all tasks
      // Set waiting state to show "waiting for task selection" message
      this.waitingForTaskSelection = true
      this.selectedTaskName = null
    },

    onChecklistStatusChanged (isComplete) {
      // Update the child task completion status
      this.childTaskComplete = isComplete
    },

    onNoActiveTask () {
      // When DesignatedPosition component has no position,
      // clear the selected task so SelectTask can show options normally
      this.selectedTaskName = null
      this.selectedTaskId = null
      localStorage.removeItem('feedAlertSelectedTaskName')
      localStorage.removeItem('feedAlertSelectedTaskId')
      this.waitingForTaskSelection = true
    },

    async onReloadSameTask () {
      // Reload the same task type without resetting the timer
      // This is triggered by the "next offer" button in Bubble/FollowUp components

      if (!this.activeTaskName) {
        return
      }

      // Follow-up mode never consumes the prefetched offer queue (see loadFollowUp).
      if (this.followUpMode) {
        await this.loadFollowUp()
        this.$nextTick(() => { this.scrollToTop() })
        return
      }

      // Feed mode: if the current time has left the Monday window the queue was built
      // for, the task switched (e.g. 10:00 בועה כחולה -> שיחות מעסיקים). Drop the stale
      // queue and re-resolve from Monday instead of serving a previous-task offer.
      if (!this.followUpMode && this.isFeedMode && !this.isNowWithinPrefetchedTaskWindow()) {
        this.$store.commit('feedPrefetch/clearQueue')
        this.handleAutomaticModeRefresh()
        this.$nextTick(() => { this.scrollToTop() })
        return
      }

      // Try to use pre-calculated offer ID (fast path)
      const nextOfferId = this.$store.getters['feedPrefetch/nextOfferId']
      if (nextOfferId) {
        const requestVersion = this.requestVersion
        await this.$store.dispatch('feedPrefetch/consumeNext')
        if (requestVersion !== this.requestVersion) return
        await this.getAlertWithPrefetchedId(nextOfferId)
        // Replenish queue in background
        this.triggerPrefetch()
        this.$nextTick(() => { this.scrollToTop() })
        return
      }

      // Fallback: old behavior (server finds the offer)
      if (this.isFeedMode) {
        this.handleAutomaticModeRefresh()
      } else {
        this.getAlert(this.selectedTaskName)
      }

      this.$nextTick(() => {
        this.scrollToTop()
      })
    },

    async getAlertWithPrefetchedId (nextOfferId) {
      if (this.followUpMode) return this.loadFollowUp()
      const requestVersion = ++this.requestVersion
      const params = {
        employee_name: this.$utils.getOwner().name,
        owner: this.$utils.getOwner().name,
        current_time: new Date().toISOString(),
        operation: this.$settings.namespace,
        next_offer_id: nextOfferId
      }
      // In feed mode use the task the queue was actually built for (resolved from Monday),
      // not the possibly-stale selectedTaskName. The window guard in onReloadSameTask
      // guarantees we only reach here while still inside that task's window.
      const prefetchMondayTask = this.$store.getters['feedPrefetch/mondayTask']
      const taskNameForServe = (this.isFeedMode && prefetchMondayTask && prefetchMondayTask.task_name) ||
        this.selectedTaskName
      if (taskNameForServe) params.task_name = taskNameForServe

      // Reuse designated position params if needed
      if (this.selectedTaskName === 'משרה ייעודית') {
        const storedState = localStorage.getItem('designated_position_state')
        if (storedState) {
          try {
            const parsed = JSON.parse(storedState)
            params.position_id = parsed.position_id
            params.offer_type = parsed.offer_type
            params.counter = parsed.counter
          } catch (e) {}
        }
      }

      try {
        const taskDisplayName = this.selectedTaskName || 'כללי'
        const requestStartTime = Date.now()
        console.log(`\n🔵 ═══ הולך לשרת להביא אלרט (prefetched) ═══`)
        console.log(`📋 משימה: ${taskDisplayName}`)
        console.log(`💼 Prefetched Offer ID: ${nextOfferId}`)
        console.log(`════════════════════════════════\n`)

        const serverAlert = await this.$network_utils.getAlertForFeedWithMonday(params)
        if (requestVersion !== this.requestVersion) return

        const requestDuration = Date.now() - requestStartTime
        const durationSeconds = (requestDuration / 1000).toFixed(2)
        const durationMinutes = Math.floor(requestDuration / 60000)
        const alertId = (serverAlert && serverAlert.admin_v1_alert && serverAlert.admin_v1_alert.id) ||
                         (serverAlert && serverAlert.id) || 'N/A'
        const offerId = (serverAlert && serverAlert.admin_v1_alert && serverAlert.admin_v1_alert.resources &&
                         serverAlert.admin_v1_alert.resources.offer && serverAlert.admin_v1_alert.resources.offer.id) ||
                        (serverAlert && serverAlert.resources && serverAlert.resources.offer && serverAlert.resources.offer.id) || 'N/A'
        const applicantName = (serverAlert && serverAlert.admin_v1_alert && serverAlert.admin_v1_alert.resources &&
                               serverAlert.admin_v1_alert.resources.applicant && serverAlert.admin_v1_alert.resources.applicant.full_name) ||
                              (serverAlert && serverAlert.resources && serverAlert.resources.applicant && serverAlert.resources.applicant.full_name) || 'N/A'

        console.log(`\n🟢 ═══ התקבלה תשובה מהשרת (prefetched) ═══`)
        console.log(`📋 משימה: ${taskDisplayName}`)
        console.log(`⏱ קריאת השרת לקחה: ${durationMinutes} דקות ו-${durationSeconds} שניות`)
        console.log(`🆔 Alert ID: ${alertId}`)
        if (offerId !== 'N/A') console.log(`💼 Offer ID: ${offerId}`)
        if (applicantName !== 'N/A') console.log(`👤 מועמד: ${applicantName}`)
        console.log(`════════════════════════════════\n`)

        this.processServerResponse(serverAlert)
      } catch (e) {
        if (requestVersion !== this.requestVersion) return
        console.error('Error with prefetched offer:', e)
        // Fallback to normal flow
        if (this.isFeedMode) {
          this.handleAutomaticModeRefresh()
        } else {
          this.getAlert(this.selectedTaskName)
        }
      }
    },

    triggerPrefetch () {
      console.log('[Prefetch] triggerPrefetch called. selectedTaskName:', this.selectedTaskName, 'alert:', !!this.alert)
      if (this.followUpMode) return
      if (!this.activeTaskName || !this.alert) {
        console.log('[Prefetch] Skipped - missing selectedTaskName or alert')
        return
      }
      const offerId = this.alert.resources && this.alert.resources.offer && this.alert.resources.offer.id
      console.log('[Prefetch] Current offer ID:', offerId, '- dispatching prefetchUpcoming')
      const context = this.buildPrefetchContext(offerId)
      this.$store.dispatch('feedPrefetch/prefetchUpcoming', { taskContext: context, vm: this })
    },

    buildPrefetchContext (currentOfferId) {
      const base = {
        current_offer_id: currentOfferId,
        count: 1,
        owner: this.$utils.getOwner().name
      }

      // Follow-up mode never prefetches (triggerPrefetch returns early); kept so
      // the context can never carry designated-position / Monday state by mistake.
      if (this.followUpMode) return { ...base, task_name: 'פולואפ כללי', operation: this.$settings.namespace }

      if (this.selectedTaskName === 'משרה ייעודית') {
        const storedState = localStorage.getItem('designated_position_state')
        if (storedState) {
          try {
            const parsed = JSON.parse(storedState)
            return { ...base, task_name: 'משרה ייעודית', position_id: parsed.position_id, offer_type: parsed.offer_type, counter: parsed.counter }
          } catch (e) {}
        }
      }

      // Feed mode: don't pin task_name — let the server resolve the current task from
      // Monday (by time), so the prefetched queue tracks task switches (10:00 boundary).
      if (this.isFeedMode) {
        return { ...base, current_time: new Date().toISOString(), operation: this.$settings.namespace }
      }

      // Manual mode: the recruiter explicitly chose the task — keep it pinned.
      return { ...base, task_name: this.selectedTaskName }
    },

    // Is `now` still inside the Monday task window the prefetch queue was built for?
    // Unknown/absent window -> false, forcing a fresh Monday resolve (safe fallback).
    isNowWithinPrefetchedTaskWindow () {
      const mondayTask = this.$store.getters['feedPrefetch/mondayTask']
      if (!mondayTask || !mondayTask.end_time) return false
      const now = this.$moment()
      const atToday = (t) => {
        const m = this.$moment(t, ['HH:mm:ss', 'HH:mm'], true)
        if (!m.isValid()) return null
        return now.clone().hour(m.hour()).minute(m.minute()).second(m.second())
      }
      const end = atToday(mondayTask.end_time)
      if (!end) return false
      const start = atToday(mondayTask.start_time)
      if (start && now.isBefore(start)) return false
      return now.isSameOrBefore(end)
    },

    switchToManualMode () {
      ++this.requestVersion
      this.$store.commit('feedPrefetch/clearQueue')
      this.isFeedMode = false
      localStorage.setItem('isFeedMode', 'false')

      localStorage.removeItem('taskStartTime')
      this.taskStartTime = null
      this.taskPassedTimeSeconds = 0

      localStorage.removeItem('feedAlertSelectedTaskName')
      localStorage.removeItem('feedAlertSelectedTaskId')
      this.selectedTaskName = null
      this.selectedTaskId = null
      this.waitingForTaskSelection = true
      if (this.followUpMode) this.loadFollowUp()
    },

    switchToFeedMode () {
      ++this.requestVersion
      this.$store.commit('feedPrefetch/clearQueue')
      this.isFeedMode = true
      localStorage.setItem('isFeedMode', 'true')

      localStorage.removeItem('taskStartTime')
      this.taskStartTime = null
      this.taskPassedTimeSeconds = 0

      localStorage.removeItem('feedAlertSelectedTaskName')
      localStorage.removeItem('feedAlertSelectedTaskId')
      this.selectedTaskName = null
      this.selectedTaskId = null
      this.waitingForTaskSelection = false

      this.getAlert()
    },

    scrollToTop () {
      // Scroll the feed alert card to the top
      const feedAlertCard = this.$el
      if (feedAlertCard) {
        // Smooth scroll to the top of the component
        feedAlertCard.scrollIntoView({ behavior: 'smooth', block: 'start' })

        // Also scroll the window to ensure visibility
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },

    async handleGoToNextTask () {
      if (!this.isFeedMode) {
        return
      }

      try {
        this.clearTaskLocalStorage()

        await this.handleAutomaticModeRefresh()

        this.$nextTick(() => {
          this.scrollToTop()
        })
      } catch (error) {
        console.error('Error loading next task:', error)
      }
    },

    reportError () {
      this.create({
        designator: 'feed-error-report-modal',
        component_name: 'FeedErrorReportModal',
        modal_title: 'דיווח על טעות בהצעה',
        modal_width: 700,
        is_center_modal: true,
        props: {
          alert: this.alert,
          selectedTaskName: this.selectedTaskName,
          selectedTaskId: this.selectedTaskId,
          operation: this.$settings.namespace,
          adminName: this.$utils.getOwner().name,
          onSubmit: async (id) => {
            this.removeModalById(id)
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.feed-alert-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 0;
  padding: 0;
  overflow: hidden;
  color: #2c3e50;
}

/* Header Section */
.alert-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 2px solid #dee2e6;
  gap: 8px;
}

.alert-title-section {
  display: flex;
  align-items: center;
  flex: 1;
}

.alert-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f1e28;
  margin: 0;
}

/* Timer Display */
.timer-display {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #e3f2fd;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1976d2;
  transition: all 0.3s ease;
}

.timer-warning {
  background: #fff3cd;
  color: #ff6b6b;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.timer-icon {
  font-size: 1.1rem;
}

.timer-text {
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

/* SelectTask Wrapper */
.select-task-wrapper {
  padding: 2px 6px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

/* Waiting Message */
.feed-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 12px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff8e1;
  border: 1px solid #f4d27a;
  color: #5c4400;
  font-size: 13px;
  direction: rtl;
}
.feed-notice-text { flex: 1; }
.feed-notice-retry { margin-inline-start: auto; }

.waiting-message {
  padding: 12px 12px;
  background: #ffffff;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.waiting-content {
  text-align: center;
  padding: 12px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 8px;
  border: 2px solid #64b5f6;
  box-shadow: 0 4px 12px rgba(100, 181, 246, 0.3);
}

.waiting-icon {
  font-size: 2rem;
  display: block;
  margin-bottom: 8px;
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.waiting-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1976d2;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Task Placeholder */
.task-placeholder {
  padding: 12px 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.task-placeholder p {
  margin: 10px 0;
  font-size: 1rem;
}

.task-placeholder strong {
  color: #3273dc;
  font-weight: 700;
}

/* Actions Section */
.alert-actions {
  display: flex;
  flex-direction: column;
  padding: 8px;
  gap: 8px;
  background: #f8f9fa;
}

.quick-actions-row {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.main-actions-row {
  display: flex;
  justify-content: space-around;
  gap: 8px;
  flex-wrap: wrap;
}

.alert-actions .button {
  font-size: 0.9rem;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: white;
  white-space: nowrap;
}

.quick-actions-row .button {
  flex: 1;
  min-width: 140px;
}

.main-actions-row .button {
  flex: 1;
  min-width: 120px;
}

.button.is-success {
  background-color: #48c774;
  border-color: #48c774;
}

.button.is-warning {
  background-color: #ffdd57;
  border-color: #ffdd57;
  color: #2c3e50;
}

.button.is-info {
  background-color: #3298dc;
  border-color: #3298dc;
}

.button.is-danger {
  background-color: #f14668;
  border-color: #f14668;
}

.button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.button .icon {
  margin-left: 6px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .alert-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .timer-display {
    align-self: stretch;
    justify-content: center;
  }

  .quick-actions-row,
  .main-actions-row {
    flex-direction: column;
  }

  .quick-actions-row .button,
  .main-actions-row .button {
    width: 100%;
    min-width: unset;
  }
}
</style>
